import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  Divider, 
  Chip, 
  alpha, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination 
} from '@mui/material';
import { MonetizationOn } from '@mui/icons-material';
import { getEdmPendingPayments } from '../../../../helpers/api';
import { properCase } from '../../../../helpers/utils';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

export default function PendingPaymentsEdmView({ stateId }) {
  // --- CORE STATE & LOGIC (UNTOUCHED) ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);

  useEffect(() => {
    fetch();
  }, [stateId]);

  const fetch = () => {
    setLoading(true);
    getEdmPendingPayments(stateId)
      .then((response) => {
        setLoading(false);
        // Retaining your slice logic
        const responseData = response.result.data?.slice(0, 12) || [];
        setData(responseData);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- UI HELPERS ---
  const getCategoryStyles = (category) => {
    switch (category?.toUpperCase()) {
      case 'MINI': return { color: '#0CB230', bg: alpha('#0CB230', 0.1) };
      case 'SMALL': return { color: '#FAAB10', bg: alpha('#FAAB10', 0.1) };
      case 'MEDUIM': return { color: '#1976d2', bg: alpha('#1976d2', 0.1) };
      case 'LARGE': return { color: '#FA1010', bg: alpha('#FA1010', 0.1) };
      default: return { color: '#5f6368', bg: '#f4f6f8' };
    }
  };

  const renderContent = () => {
    if (loading) return <Box sx={{ p: 2 }}><LoaderSkeleton /></Box>;

    if (!loading && data.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
          <Typography variant="body2">No outstanding payments found</Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table size="medium">
          <TableHead sx={{ bgcolor: '#f9fafb' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Facility</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }} align="center">Category</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }} align="right">Outstanding</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const styles = getCategoryStyles(row.categoryName);
                return (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.facilityName}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={properCase(row.categoryName)} 
                        size="small"
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: '0.75rem',
                          color: styles.color,
                          bgcolor: styles.bg,
                          border: `1px solid ${alpha(styles.color, 0.2)}`,
                          borderRadius: '8px',
                          width: '75px'
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {Intl.NumberFormat('en-NG', {
                        style: 'currency',
                        currency: 'NGN',
                        maximumFractionDigits: 0
                      }).format(row.totalOutstandingAmount)}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[6, 12]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    );
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        height: '100%', 
        borderRadius: '16px', 
        border: '1px solid', 
        borderColor: 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box 
          sx={{ 
            p: 1, 
            borderRadius: '10px', 
            bgcolor: alpha('#FA1010', 0.1), 
            color: '#FA1010',
            display: 'flex'
          }}
        >
          <MonetizationOn fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#5F6368', lineHeight: 1.2 }}>
            Outstanding Payments
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Arrears breakdown by facility
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1 }}>
        {renderContent()}
      </Box>
    </Card>
  );
}